
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Explore Plaid products from the Dashboard, without writing code or handling API secrets'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Header title='Sandbox Studio' subtitle={metadata.description} mdxType="Header" />
    <Actions.SmallWrapper>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='Open Sandbox Studio' link='https://dashboard.plaid.com/developers/sandbox' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`The Sandbox Studio tool in the Plaid Dashboard, under `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/sandbox"
      }}>{`Build -> Sandbox`}</a>{`, is a no-code way to work with Sandbox data. In the Dashboard, you can browse Plaid's built-in `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials"
      }}>{`test users`}</a>{`, create and edit your own custom users, access and manage `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles"
      }}>{`Sandbox profiles`}</a>{` for testing Layer and returning user flows, create an Item for any user by launching Link or by bypassing Link entirely, and call Sandbox endpoints against that Item -- either individually or as part of a guided multi-step scenario -- to view the full request and response. Your Sandbox API keys and the default test credentials are also shown on the page.`}</p>
    <h4 {...{
      "id": "sandbox-test-users"
    }}>{`Sandbox test users`}</h4>
    <p>{`Sandbox Studio manages two kinds of test users.`}</p>
    <p>{`One is Plaid's `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials"
      }}>{`pre-populated test users`}</a>{`, marked with a robot icon, which may contain dynamic data, but which you cannot edit. For more details, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials"
      }}>{`test credentials`}</a>{`.`}</p>
    <p>{`The other is `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`custom users`}</a>{` that you define, marked with a light bulb icon. Within the Dashboard, you can create, manage, and edit these users. Custom test users can be generated in the Dashboard from a prompt or a template, or by manually configuring JSON.`}</p>
    <Image src='/assets/img/docs/sandbox-studio/sandbox-studio-describe-user.png' alt='The Create user page in Sandbox Studio, with a plain-English description of the user to generate, a Templates dropdown, and the resulting configuration object.' expandable={true} mdxType="Image" />
    <p>{`From any user you can `}<strong parentName="p">{`Launch Link`}</strong>{`, to run the Link flow with that user's credentials pre-filled, or `}<strong parentName="p">{`Skip Link`}</strong>{` to create an Item by `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{`.`}</p>
    <Image src='/assets/img/docs/sandbox-studio/sandbox-studio-users.png' alt="The Users tab of Sandbox Studio, listing built-in and custom test users alongside the team's Sandbox keys and credentials for the currently selected user." expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "sandbox-profiles"
    }}>{`Sandbox Profiles`}</h4>
    <p>{`The `}<strong parentName="p">{`Profiles`}</strong>{` tab contains `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/"
      }}>{`Sandbox Profiles`}</a>{`, which are saved returning-user profiles. A test user corresponds to a bank login, while a Sandbox profile corresponds to a specific phone number and represents a user who has connected accounts to Plaid before.`}</p>
    <p>{`Sandbox Profiles allow you to test the `}<a parentName="p" {...{
        "href": "/docs/link/returning-user/"
      }}>{`returning user experience`}</a>{` and `}<a parentName="p" {...{
        "href": "/docs/layer/"
      }}>{`Layer`}</a>{` against a realistic set of connected accounts.`}</p>
    <p>{`For more details on testing with Sandbox Profiles, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/"
      }}>{`Sandbox Profiles`}</a>{`.`}</p>
    <Image src='/assets/img/docs/sandbox-studio/sandbox-profiles-tab.png' alt="The Profiles tab of Sandbox Studio, showing the list of custom and starter profiles and a selected profile's phone number, connected Item, and options to launch Link or Layer." expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "api-explorer"
    }}>{`API explorer`}</h4>
    <p>{`The API explorer runs live Sandbox calls against an Item and shows the full request and response. Every request can also be copied as a cURL command to run yourself.`}</p>
    <p>{`The explorer has two tabs. `}<strong parentName="p">{`Endpoints`}</strong>{` lets you call any of a curated set of major product API endpoints directly. `}<strong parentName="p">{`Scenarios`}</strong>{`, scoped to a specific product, walks you through a multi-step flow using an Item and request payload that are pre-configured to work out of the box.`}</p>
    <Image src='/assets/img/docs/sandbox-studio/sandbox-studio-scenarios.png' alt="The Scenarios tab of the API explorer with the Statements scenario selected, showing the scenario's Item and access token, its ordered steps for listing, refreshing, and downloading statements, and the request and response panels for /statements/list." expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "configuration"
    }}>{`Configuration`}</h4>
    <p>{`The configuration page allows you to customize the parameters used by the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call used by `}<strong parentName="p">{`Launch Link`}</strong>{` (including important parameters like `}<inlineCode parentName="p">{`products`}</inlineCode>{`, which can impact the set of institutions available in Link), as well as the `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` overrides used by `}<strong parentName="p">{`Skip Link`}</strong>{`. Changing configuration here does not affect your integration; it only changes the calls Sandbox Studio makes. For more details on the configuration options avilable here, see the documentation for `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. `}</p>
    <Image src='/assets/img/docs/sandbox-studio/sandbox-studio-configuration.png' alt="The Sandbox configuration page, with the connection type set to Plaid Link and controls for the /link/token/create parameters, including Link customization, client name, country codes, and the products requested." expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "using-the-sandbox-api-directly"
    }}>{`Using the Sandbox API directly`}</h4>
    <p>{`Sandbox Studio is provided as a convenience feature for managing test users and making API calls without writing code. Most flows available via Sandbox Studio are also available directly via the API.`}</p>
    <p>{`The exception is the ability to manage and reset Sandbox Profiles, which is available only via the Dashboard.`}</p>
    <p>{`To access API explorer-style functionality in a dedicated tool like Postman or Bruno instead, see the `}<a parentName="p" {...{
        "href": "/docs/api/postman/"
      }}>{`Plaid Postman collection`}</a>{`, which contains similar functionality.`}</p>
    <p>{`To manage Sandbox custom users directly via the API, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/#configuring-the-custom-user-account"
      }}>{`Configuring the custom user account`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "sandbox-test-users",
        level: 4,
        title: "Sandbox test users",
      },
{
        id: "sandbox-profiles",
        level: 4,
        title: "Sandbox Profiles",
      },
{
        id: "api-explorer",
        level: 4,
        title: "API explorer",
      },
{
        id: "configuration",
        level: 4,
        title: "Configuration",
      },
{
        id: "using-the-sandbox-api-directly",
        level: 4,
        title: "Using the Sandbox API directly",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
